/**
 * Data Link Dialog — modal for creating/managing data sources and links
 */
import { dataStore } from '../stores/dataStore';
import { projectStore } from '../stores/projectStore';
import { monitorStore } from '../stores/monitorStore';
import { templateRegistry } from '../engine/templateRegistry';
import { parseCSV } from '../engine/dataLinker';
import type { DataSource, TemplateField } from '@graphicsim/shared-types';

let dialogEl: HTMLElement | null = null;

export function initDataLinkDialog(): void {
  // Link Data button
  const linkBtn = document.querySelector('.link-btn');
  linkBtn?.addEventListener('click', openDialog);
}

export function openDialog(): void {
  if (dialogEl) return; // already open

  dialogEl = document.createElement('div');
  dialogEl.className = 'modal-overlay';
  dialogEl.innerHTML = `
    <div class="modal-dialog data-link-dialog">
      <div class="modal-header">
        <span class="modal-title">Data Link Manager</span>
        <button class="modal-close">&times;</button>
      </div>
      <div class="modal-body">
        <div class="dl-section">
          <h3 class="dl-section-title">Data Sources</h3>
          <div class="dl-sources-list"></div>
          <div class="dl-source-actions">
            <button class="dl-btn dl-add-manual">+ Manual Source</button>
            <button class="dl-btn dl-add-csv">+ Import CSV</button>
          </div>
        </div>
        <div class="dl-divider"></div>
        <div class="dl-section">
          <h3 class="dl-section-title">Field Links</h3>
          <div class="dl-links-list"></div>
          <div class="dl-link-actions">
            <button class="dl-btn dl-add-link">+ Add Link</button>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(dialogEl);

  // Close button
  dialogEl.querySelector('.modal-close')?.addEventListener('click', closeDialog);
  dialogEl.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).classList.contains('modal-overlay')) closeDialog();
  });

  // Action buttons
  dialogEl.querySelector('.dl-add-manual')?.addEventListener('click', addManualSource);
  dialogEl.querySelector('.dl-add-csv')?.addEventListener('click', addCSVSource);
  dialogEl.querySelector('.dl-add-link')?.addEventListener('click', addLink);

  renderSources();
  renderLinks();
}

function closeDialog(): void {
  dialogEl?.remove();
  dialogEl = null;
}

function renderSources(): void {
  if (!dialogEl) return;
  const list = dialogEl.querySelector('.dl-sources-list')!;
  const sources = dataStore.getState().sources;

  if (sources.length === 0) {
    list.innerHTML = `<div class="dl-empty">No data sources. Create one to link data to template fields.</div>`;
    return;
  }

  list.innerHTML = sources.map((src) => `
    <div class="dl-source-item" data-source-id="${src.id}">
      <div class="dl-source-header">
        <span class="dl-source-name">${src.name}</span>
        <span class="dl-source-type">${src.type}</span>
        <button class="dl-source-remove" data-source-id="${src.id}">&times;</button>
      </div>
      <div class="dl-source-info">
        ${src.columns.length} columns, ${src.rows.length} rows
        ${src.rows.length > 0 ? `(Row ${src.activeRowIndex + 1} active)` : ''}
      </div>
      ${src.rows.length > 1 ? `
        <div class="dl-source-nav">
          <button class="dl-row-prev" data-source-id="${src.id}">&#x25C0;</button>
          <span>Row ${src.activeRowIndex + 1} / ${src.rows.length}</span>
          <button class="dl-row-next" data-source-id="${src.id}">&#x25B6;</button>
        </div>
      ` : ''}
    </div>
  `).join('');

  // Remove buttons
  list.querySelectorAll<HTMLButtonElement>('.dl-source-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      dataStore.getState().removeSource(btn.dataset.sourceId!);
      renderSources();
      renderLinks();
    });
  });

  // Row navigation
  list.querySelectorAll<HTMLButtonElement>('.dl-row-prev').forEach((btn) => {
    btn.addEventListener('click', () => {
      const src = dataStore.getState().sources.find((s) => s.id === btn.dataset.sourceId);
      if (src && src.activeRowIndex > 0) {
        dataStore.getState().updateSourceRow(src.id, src.activeRowIndex - 1);
        renderSources();
      }
    });
  });
  list.querySelectorAll<HTMLButtonElement>('.dl-row-next').forEach((btn) => {
    btn.addEventListener('click', () => {
      const src = dataStore.getState().sources.find((s) => s.id === btn.dataset.sourceId);
      if (src && src.activeRowIndex < src.rows.length - 1) {
        dataStore.getState().updateSourceRow(src.id, src.activeRowIndex + 1);
        renderSources();
      }
    });
  });
}

function renderLinks(): void {
  if (!dialogEl) return;
  const list = dialogEl.querySelector('.dl-links-list')!;
  const links = dataStore.getState().links;
  const sources = dataStore.getState().sources;

  if (links.length === 0) {
    list.innerHTML = `<div class="dl-empty">No links. Link data source columns to template fields.</div>`;
    return;
  }

  list.innerHTML = links.map((link) => {
    const source = sources.find((s) => s.id === link.dataSourceId);
    const item = projectStore.getState().getItem(link.projectItemId);
    return `<div class="dl-link-item">
      <span>${source?.name ?? '?'}.${link.columnName}</span>
      <span class="dl-link-arrow">&#x2192;</span>
      <span>${item?.name ?? '?'}.${link.fieldId}</span>
      <button class="dl-link-remove" data-link-id="${link.id}">&times;</button>
    </div>`;
  }).join('');

  list.querySelectorAll<HTMLButtonElement>('.dl-link-remove').forEach((btn) => {
    btn.addEventListener('click', () => {
      dataStore.getState().removeLink(btn.dataset.linkId!);
      renderLinks();
    });
  });
}

function addManualSource(): void {
  const name = prompt('Data source name:', 'Game Data');
  if (!name) return;
  const colStr = prompt('Column names (comma-separated):', 'homeTeam,awayTeam,homeScore,awayScore');
  if (!colStr) return;

  const columns = colStr.split(',').map((c) => c.trim()).filter(Boolean);
  const row: Record<string, string> = {};
  columns.forEach((c) => { row[c] = ''; });

  dataStore.getState().addSource({
    name,
    type: 'manual',
    columns,
    rows: [row],
    activeRowIndex: 0,
  });
  renderSources();
}

function addCSVSource(): void {
  const name = prompt('Data source name:', 'CSV Import');
  if (!name) return;

  // Create a file input
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.csv';
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const { columns, rows } = parseCSV(text);
      if (columns.length === 0) {
        alert('No columns found in CSV');
        return;
      }
      dataStore.getState().addSource({
        name,
        type: 'csv',
        columns,
        rows,
        activeRowIndex: 0,
      });
      renderSources();
    };
    reader.readAsText(file);
  });
  input.click();
}

function addLink(): void {
  if (!dialogEl) return;

  const sources = dataStore.getState().sources;
  const items = projectStore.getState().items;

  if (sources.length === 0) {
    alert('Create a data source first');
    return;
  }
  if (items.length === 0) {
    alert('Add items to the project first');
    return;
  }

  // Build link creation form
  const form = document.createElement('div');
  form.className = 'dl-link-form';
  form.innerHTML = `
    <div class="dl-form-row">
      <label>Source:</label>
      <select class="dl-select" id="dl-source-select">
        ${sources.map((s) => `<option value="${s.id}">${s.name}</option>`).join('')}
      </select>
    </div>
    <div class="dl-form-row">
      <label>Column:</label>
      <select class="dl-select" id="dl-column-select">
        ${sources[0].columns.map((c) => `<option value="${c}">${c}</option>`).join('')}
      </select>
    </div>
    <div class="dl-form-row">
      <label>Project Item:</label>
      <select class="dl-select" id="dl-item-select">
        ${items.map((i) => `<option value="${i.id}">${i.name}</option>`).join('')}
      </select>
    </div>
    <div class="dl-form-row">
      <label>Field:</label>
      <select class="dl-select" id="dl-field-select"></select>
    </div>
    <div class="dl-form-actions">
      <button class="dl-btn dl-form-ok">Create Link</button>
      <button class="dl-btn dl-form-cancel">Cancel</button>
    </div>
  `;

  const linkActions = dialogEl.querySelector('.dl-link-actions')!;
  linkActions.after(form);

  // Update columns when source changes
  const sourceSelect = form.querySelector('#dl-source-select') as HTMLSelectElement;
  const columnSelect = form.querySelector('#dl-column-select') as HTMLSelectElement;
  const itemSelect = form.querySelector('#dl-item-select') as HTMLSelectElement;
  const fieldSelect = form.querySelector('#dl-field-select') as HTMLSelectElement;

  sourceSelect.addEventListener('change', () => {
    const src = sources.find((s) => s.id === sourceSelect.value);
    columnSelect.innerHTML = (src?.columns ?? []).map((c) => `<option value="${c}">${c}</option>`).join('');
  });

  // Update fields when item changes
  function updateFields() {
    const item = items.find((i) => i.id === itemSelect.value);
    if (!item) return;
    const template = templateRegistry.getTemplate(item.templateId);
    if (!template) return;
    fieldSelect.innerHTML = template.fields.map((f) => `<option value="${f.id}">${f.label}</option>`).join('');
  }
  itemSelect.addEventListener('change', updateFields);
  updateFields();

  // OK/Cancel
  form.querySelector('.dl-form-ok')?.addEventListener('click', () => {
    dataStore.getState().addLink({
      dataSourceId: sourceSelect.value,
      columnName: columnSelect.value,
      projectItemId: itemSelect.value,
      fieldId: fieldSelect.value,
    });
    form.remove();
    renderLinks();
  });
  form.querySelector('.dl-form-cancel')?.addEventListener('click', () => form.remove());
}
